import { useEffect } from "react";
import { Control, useWatch } from "react-hook-form";
import { ValuationsFormSchemaType } from "../valuations-form";
import { ControlledTextInput } from "../../../components/forms/controlled-text-input";
import { InlineTextButton } from "../../../components/buttons/text-button/inline-text-button";
import { FontStyles } from "../../../utils/font-styles";
import { usePostcodeLookup } from "./use-postcode-lookup";
import { ErrorMessage } from "../../../components/alerts/error-message";
import "../../../../assets/fa-icons/css/fontawesome.css";
import "../../../../assets/fa-icons/css/sharp-regular.css";

interface ManualAddressFormProps {
  control: Control<ValuationsFormSchemaType>;
  onBackToLookup: () => void;
  onPostcodeError?: (error: "NOT_FOUND" | "OUTSIDE_SERVICE_AREA" | null) => void;
}

export const ManualAddressForm = ({ control, onBackToLookup, onPostcodeError }: ManualAddressFormProps) => {
  // Watch postcode value to trigger validation
  const postcode = useWatch({ control, name: "postcode" });

  // Use the same postcode lookup hook as lookup mode
  const { error, isLoading } = usePostcodeLookup(postcode);

  // Notify parent component of postcode error state changes
  useEffect(() => {
    onPostcodeError?.(error);
  }, [error, onPostcodeError]);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-x-4 md:gap-y-4.5">
        <ControlledTextInput
          control={control}
          name="addressLine1"
          placeholder="Enter 1st line of address"
          label="Address line 1"
          showRequiredAsterisk
        />
        <ControlledTextInput
          control={control}
          name="addressLine2"
          placeholder="Enter 2nd line of address"
          label="Address line 2"
        />
        <ControlledTextInput
          control={control}
          name="postTown"
          placeholder="Enter town or city"
          label="Town / City"
          showRequiredAsterisk
        />
        <ControlledTextInput
          control={control}
          name="postcode"
          placeholder="Postcode"
          label="Postcode"
          showRequiredAsterisk
          state={error === "OUTSIDE_SERVICE_AREA" ? "error" : undefined}
          renderTrailingIcon={
            isLoading ? () => <i className="fa-sharp fa-regular fa-spinner-third text-base animate-spin" /> : undefined
          }
        />
        <div>
          <i className={`fa-sharp fa-regular fa-arrow-left text-loch ${FontStyles["body-md"]}`} />{" "}
          <InlineTextButton onClick={onBackToLookup}>Back to address lookup</InlineTextButton>
        </div>
      </div>
      {error === "OUTSIDE_SERVICE_AREA" && (
        <ErrorMessage>Sorry, this postcode is not covered by any ESPC solicitor estate agents.</ErrorMessage>
      )}
    </div>
  );
};
